import * as enabledApi from '@shared/api/enabledPlayApi';
import { getCustomControlModels } from '@shared/constants/controls/CustomControlUtils';
import { CUSTOM_CONTROLS_STORAGE } from '@shared/constants/Keys';
import { DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import MacroModel from '@shared/models/deviceProfiles/MacroModel';
import { AvailableCustomControlsModel } from '@shared/models/devices/AvailableCustomControlsModel';
import { CustomControlModel } from '@shared/models/devices/CustomControlModel';
import { CustomControlsModel } from '@shared/models/devices/CustomControlsModel';
import UserDeviceCustomControlOverrideModel from '@shared/models/devices/UserDeviceCustomControlOverrideModel';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import UpdateUserLocaleRequest from '@shared/models/users/UpdateUserLocaleRequest';
import { UserAppInterfaceControlOverride } from '@shared/models/users/UserAppInterfaceControlOverride';
import { randomUUID } from 'crypto';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createContainer } from 'unstated-next';
import { publish, subscribe } from '../helpers/EventCallback';
import useStateRef from '../useStateRef';
import CurrentDeviceContainer from './CurrentDeviceContainer';
import DeviceContainer from './DeviceContainer';

function useGlobalCustomControlsContainer() {
    const deviceContainer = DeviceContainer.useContainer();
    const currentDeviceContainer = CurrentDeviceContainer.useContainer();
    const [availableCustomControls, setAvailableCustomControls, availableCustomControlsRef] = useStateRef<
        AvailableCustomControlsModel[] | null
    >(null);
    const userLocale = useRef<string>(DefaultVoiceLocale);
    const [profileCustomControls, setProfileCustomControls, profileCustomControlsRef] = useStateRef<
        AvailableCustomControlsModel[] | null
    >(null);
    const customControlsLocaleRef = useRef<string>(DefaultVoiceLocale);
    const voicePausedCustomControlsRef = useRef(new Set<string>());
    const voiceDictationCustomControlsRef = useRef(new Set<string>());
    const voicePrefixCustomControlsRef = useRef(new Set<string>());
    const devicesCountRef = useRef(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [resetDeviceControls, setResetDeviceControls] = useState<boolean>(false);

    const handleVoiceDictationControlsUpdate = (dictationControls: Set<string>) => {
        voiceDictationCustomControlsRef.current = dictationControls;
        publish('update-voice-dictation-controls', dictationControls);
    };

    const initializeFromStorage = (localePreferences: UpdateUserLocaleRequest) => {
        try {
            setIsLoading(true);
            const storedControls = localStorage.getItem(CUSTOM_CONTROLS_STORAGE);
            let available: AvailableCustomControlsModel[] = [];
            if (storedControls) {
                const parsedControls = JSON.parse(storedControls);
                const devices = deviceContainer.devicesRef.current;

                if (devices) {
                    for (let device of devices) {
                        const storedDeviceControls = parsedControls.available.find(
                            (d: AvailableCustomControlsModel) => d.deviceId === device.id
                        );

                        if (storedDeviceControls) {
                            let controls: CustomControlModel[] = storedDeviceControls.controls;

                            controls = storedDeviceControls.controls;

                            available.push({
                                deviceId: device.id,
                                deviceTypeId: device.device?.deviceType?.id || device.id,
                                name: deviceContainer.getDeviceName(device.id),
                                controls: controls,
                            });
                        }
                    }
                }

                const globalControls = parsedControls.available.find(
                    (d: AvailableCustomControlsModel) => d.deviceId === 'global'
                );
                if (globalControls) {
                    available.push({
                        deviceId: 'global',
                        deviceTypeId: 'global',
                        name: 'Cephable',
                        controls: globalControls.controls,
                    });
                }

                setAvailableCustomControls(available);
                setProfileCustomControls(parsedControls.profile);
                customControlsLocaleRef.current = localePreferences.defaultVoiceLocale;
                voicePausedCustomControlsRef.current = new Set(parsedControls.paused);
                handleVoiceDictationControlsUpdate(new Set(parsedControls.dictation));
                voicePrefixCustomControlsRef.current = new Set(parsedControls.prefix);
            }
        } catch (e) {
            console.log(e);
            setErrors(['Failed to parse custom controls from local storage.']);
        } finally {
            setIsLoading(false);
        }
    };

    const initializeFromRemote = async (localePreferences: UpdateUserLocaleRequest, virtualDeviceId: string | null) => {
        userLocale.current = localePreferences.defaultVoiceLocale;
        try {
            setIsLoading(true);
            const locale = localePreferences.defaultVoiceLocale;
            customControlsLocaleRef.current = locale;
            const interfaceResult = await enabledApi.getUserAppInterfaceControls(locale);
            // get virtual device controls from API because of seeded device type controls (ie Open App)
            const virtualDeviceResult = virtualDeviceId
                ? await enabledApi.getDeviceCustomControls(virtualDeviceId, locale)
                : new UnexpectedResult<CustomControlsModel>();

            let available: AvailableCustomControlsModel[] = [];
            let profile: AvailableCustomControlsModel[] = [];
            const devices = deviceContainer.devicesRef.current;

            if (devices) {
                for (let device of devices) {
                    if (device.isVerified === false) continue;
                    const deviceType = device.device?.deviceType;
                    // If there are no custom controls for the device, check if there are device type controls to seed it with
                    if (
                        deviceType &&
                        device.customControls.length === 0 &&
                        device.customDeviceTypeControlOverrides.length === 0
                    ) {
                        const deviceTypeControlsResult = await enabledApi.getDeviceTypeCustomControls(
                            deviceType.id,
                            locale
                        );
                        if (deviceTypeControlsResult?.resultType === 'Ok' && deviceTypeControlsResult.data) {
                            device.customControls = deviceTypeControlsResult.data.controls.map(
                                (c) =>
                                    ({
                                        ...c,
                                        isDeviceTypeControl: true,
                                    }) as CustomControlModel
                            );
                        }
                    }
                    if (device.customControls.length === 0 && device.customDeviceTypeControlOverrides.length === 0) {
                        continue;
                    }

                    const deviceId = device.id;
                    const deviceName = deviceContainer.getDeviceName(deviceId);
                    const isPrivate = deviceType?.isPrivate;

                    let controls: CustomControlModel[];

                    if (
                        deviceId === virtualDeviceId &&
                        virtualDeviceResult?.resultType === 'Ok' &&
                        virtualDeviceResult.data
                    ) {
                        controls = virtualDeviceResult.data.controls;
                    } else {
                        controls = getCustomControlModels(
                            device.customControls,
                            device.customDeviceTypeControlOverrides
                        );
                    }

                    available.push({
                        deviceId: device.id,
                        deviceTypeId: deviceType.id,
                        name: deviceName,
                        isPrivate: isPrivate,
                        controls: controls,
                    });
                    const profileControls = controls.filter((c) => c.isProfileControl);
                    if (profileControls.length > 0) {
                        profile.push({
                            deviceId: device.id,
                            deviceTypeId: deviceType.id,
                            name: deviceName,
                            isPrivate: isPrivate,
                            controls: profileControls,
                        });
                    }
                }
            }

            if (interfaceResult?.resultType == 'Ok' && interfaceResult.data) {
                // Add global interface controls
                const interfaceControlRemote: CustomControlsModel = interfaceResult.data;
                const globalDevice: AvailableCustomControlsModel = {
                    deviceId: 'global',
                    deviceTypeId: 'global',
                    name: 'Cephable',
                    isPrivate: false,
                    controls: interfaceControlRemote.controls,
                };
                available.push(globalDevice);
                const profileInterface = interfaceResult.data.controls.filter((c) => c.isProfileControl);
                if (profileInterface.length > 0) {
                    profile.push(globalDevice);
                }
            }

            setProfileCustomControls(profile);
            setAvailableCustomControls(available);
            resetVoiceCustomControls();
            snapshotState();
            return new SuccessResult<boolean>(true);
        } catch (error) {
            console.log(error);
            setErrors(errors);
            return new UnexpectedResult<boolean>();
        } finally {
            setIsLoading(false);
        }
    };

    async function getDeviceCustomControls(userDeviceId: string) {
        try {
            const result = await enabledApi.getDeviceCustomControls(userDeviceId, customControlsLocaleRef.current);
            return result;
        } catch (e) {
            console.log(e);
            setErrors(['Error getting device custom controls']);
            return new UnexpectedResult<CustomControlsModel>();
        }
    }

    const currentDeviceControls: {
        global: CustomControlModel[] | undefined;
        device: CustomControlModel[] | undefined;
    } = useMemo(() => {
        const deviceControls: CustomControlModel[] = [];
        const global = availableCustomControlsRef.current?.find((d) => d.deviceId === 'global')?.controls;
        const device = availableCustomControlsRef.current?.find(
            (d) => d.deviceId === currentDeviceContainer.id
        )?.controls;
        if (device) deviceControls.push(...device);
        if (global) deviceControls.push(...global);
        return { global, device };
    }, [availableCustomControls, currentDeviceContainer.id]);

    function snapshotState() {
        localStorage.setItem(
            CUSTOM_CONTROLS_STORAGE,
            JSON.stringify({
                available: availableCustomControlsRef.current,
                profile: profileCustomControlsRef.current,
                paused: [...voicePausedCustomControlsRef.current],
                dictation: [...voiceDictationCustomControlsRef.current],
                prefix: [...voicePrefixCustomControlsRef.current],
            })
        );
    }

    function resetVoiceCustomControls() {
        voicePausedCustomControlsRef.current.clear();
        handleVoiceDictationControlsUpdate(new Set());
        voicePrefixCustomControlsRef.current.clear();
        let paused: string[] = [];
        let dictation: string[] = [];
        let prefix: string[] = [];

        availableCustomControlsRef.current?.forEach((deviceControls) => {
            // ignore general DeviceType controls but allow global controls
            if (deviceControls.deviceId !== deviceControls.deviceTypeId || deviceControls.deviceId === 'global') {
                const voicePausedControls = deviceControls.controls.filter((c) => c.allowWhenVoicePaused);
                const voiceDictationControls = deviceControls.controls.filter((c) => c.allowWhenDictating);
                const voicePrefixControls = deviceControls.controls.filter((c) => c.isPrefixVoiceCommand);

                voicePausedControls.forEach((c) => {
                    if (c.commands) paused.push(...c.commands);
                });

                voiceDictationControls.forEach((c) => {
                    if (c.commands) dictation.push(...c.commands);
                });

                voicePrefixControls.forEach((c) => {
                    if (c.commands) prefix.push(...c.commands);
                });
            }
        });

        voicePausedCustomControlsRef.current = new Set(paused);
        handleVoiceDictationControlsUpdate(new Set(dictation));
        voicePrefixCustomControlsRef.current = new Set(prefix);
    }

    function getDeviceControlName(controlId: string) {
        if (!availableCustomControlsRef.current) return controlId;
        for (let device in availableCustomControlsRef.current) {
            for (let control of availableCustomControlsRef.current[device].controls) {
                if (control.id == controlId) {
                    return control.name;
                }
            }
        }
        return controlId;
    }

    function updateAvailableCustomControls(
        index: number,
        controls: CustomControlModel[],
        deviceId: string,
        deviceTypeId: string,
        name: string
    ) {
        if (availableCustomControlsRef.current) {
            if (index < 0) {
                setAvailableCustomControls([
                    ...availableCustomControlsRef.current,
                    {
                        deviceId,
                        deviceTypeId,
                        name,
                        controls,
                    },
                ]);
            } else {
                availableCustomControlsRef.current[index].controls = controls;
            }
            setAvailableCustomControls([...availableCustomControlsRef.current]);
        }
        resetVoiceCustomControls();
        snapshotState();
    }

    async function updateAppInterfaceControls(overrides: UserAppInterfaceControlOverride[]) {
        const allAppInterfaceControls =
            availableCustomControlsRef.current?.find((d) => d.deviceId === 'global')?.controls || [];

        const sendAppInterfaceControls: UserAppInterfaceControlOverride[] = allAppInterfaceControls.map((c) => {
            const override = overrides.find((o) => o.appInterfaceControlId === c.id);
            return {
                appInterfaceControlId: c.id,
                commands: override?.commands || c?.commands || [],
            };
        });

        try {
            const result = await enabledApi.updateUserAppInterfaceControls(
                sendAppInterfaceControls,
                customControlsLocaleRef.current
            );
            if (result?.resultType === 'Ok' && result.data) {
                updateLocalAppInterfaceControls(result.data);

                return result;
            }
        } catch (e) {
            console.log(e);
            setErrors(['Error updating app interface controls']);
            return new UnexpectedResult<CustomControlsModel>();
        }
    }

    async function updateDeviceCustomControls(deviceId: string, overrides: UserDeviceCustomControlOverrideModel[]) {
        if (!availableCustomControlsRef.current) return;
        const allDeviceControls = availableCustomControlsRef.current.find((d) => d.deviceId === deviceId)?.controls;
        if (!allDeviceControls) return;
        const sendDeviceControls: UserDeviceCustomControlOverrideModel[] = allDeviceControls.map((c) => {
            const override = overrides.find((o) => o.deviceTypeCustomControlId === c.id);
            return {
                deviceTypeCustomControlId: c.id,
                commands: override?.commands || c?.commands || [],
            };
        });
        try {
            const result = await enabledApi.updateDeviceCustomControls(
                deviceId,
                sendDeviceControls,
                customControlsLocaleRef.current
            );
            if (result.resultType === 'Ok' && result.data) {
                updateLocalDeviceCustomControls(result.data);
                return result;
            }
        } catch (e) {
            console.log(e);
            setErrors(['Error updating device custom controls']);
            return new UnexpectedResult<CustomControlsModel>();
        }
    }

    async function updateCustomUserDeviceControls(
        deviceId: string,
        controlsToUpdate: CustomControlModel[],
        locale: string
    ) {
        if (!deviceId || !controlsToUpdate) {
            return new UnexpectedResult<CustomControlsModel>();
        }

        if (controlsToUpdate.length === 0) return;

        try {
            const result = await enabledApi.updateCustomUserDeviceControls(deviceId, controlsToUpdate, locale);
        } catch (e) {
            console.log(e);
            setErrors([`Error updating device custom controls`]);
            return new UnexpectedResult<CustomControlsModel>();
        }
    }

    function updateLocalAppInterfaceControls(appInterfaceControls: CustomControlsModel) {
        if (!appInterfaceControls) return;
        const deviceIndex = availableCustomControlsRef.current?.findIndex((d) => d.deviceId === 'global');
        if (deviceIndex == undefined) return;
        updateAvailableCustomControls(deviceIndex, appInterfaceControls.controls, 'global', 'global', 'Cephable');
    }

    function updateLocalDeviceCustomControls(deviceCustomControls: CustomControlsModel) {
        const deviceId = deviceCustomControls?.userDeviceId;
        if (!deviceId) return;
        const deviceIndex = availableCustomControlsRef.current?.findIndex((d) => d.deviceId === deviceId);
        const deviceName = deviceContainer.getDeviceName(deviceId);
        if (deviceIndex == undefined) return;
        updateAvailableCustomControls(deviceIndex, deviceCustomControls.controls, deviceId, deviceId, deviceName);
    }

    function convertCustomControlToMacro(
        customControls: AvailableCustomControlsModel,
        control: CustomControlModel,
        additionalInputContent?: string
    ) {
        const macro: MacroModel = {
            id: control.id,
            name: control.name,
            commands: control.commands || [],
            events: [
                {
                    eventType: 'DeviceTypeCustomAction',
                    additionalInputContent,
                    deviceTypeCustomActionId: control.id,
                    deviceTypeId: customControls.deviceId,
                },
            ],
        };
        return macro;
    }
    function separatePrefixCommand(command: string): { prefix: string; phrase: string } | undefined {
        if (!voicePrefixCustomControlsRef.current || !command) return undefined;
        for (let prefix of voicePrefixCustomControlsRef.current) {
            if (command.toLowerCase().startsWith(prefix)) {
                let additionalInputStartIndex = prefix.length;
                while (command[additionalInputStartIndex] === ' ') {
                    additionalInputStartIndex++;
                }
                const phrase = command?.substring(additionalInputStartIndex);
                return { prefix, phrase };
            }
        }
        return undefined;
    }

    function cleanCommand(command: string): string {
        return command.toLowerCase().trim();
    }

    function findCustomControl(
        command: string,
        currentDeviceId: string,
        isEligibleControl: (control: CustomControlModel) => boolean,
        additionalInputContent?: string
    ) {
        if (!availableCustomControlsRef.current) return null;
        for (const deviceControls of availableCustomControlsRef.current) {
            if (deviceControls.deviceId === currentDeviceId || deviceControls.deviceId === 'global') {
                for (const control of deviceControls.controls) {
                    if (isEligibleControl(control) && control.commands?.includes(command)) {
                        const macro = convertCustomControlToMacro(deviceControls, control, additionalInputContent);
                        return macro;
                    }
                }
            }
        }
        return null;
    }

    function getFinalControlMacro(
        command: string,
        currentDeviceId: string,
        additionalInputContent?: string,
        isEligibleControl?: (control: CustomControlModel) => boolean
    ) {
        // find any macro control and account for prefix controls
        const cleanedCommand = cleanCommand(command);
        if (cleanedCommand.length === 0) return null;

        const cleanedPrefixCommand = separatePrefixCommand(command);
        let effectiveCommand = cleanedCommand;
        if (cleanedPrefixCommand) {
            effectiveCommand = cleanedPrefixCommand.prefix;
            additionalInputContent = cleanedPrefixCommand.phrase;
        }

        const isEligible = isEligibleControl ?? ((control: CustomControlModel) => true);
        return findCustomControl(effectiveCommand, currentDeviceId, isEligible, additionalInputContent);
    }

    function getFinalDictationControlMacro(
        command: string,
        currentDeviceId: string,
        isPaused: boolean,
        additionalInputContent?: string
    ) {
        // find controls that are not prefix controls
        const cleanedCommand = cleanCommand(command);
        if (cleanedCommand.length === 0) return null;

        const cleanedPrefixCommand = separatePrefixCommand(command);
        let effectiveCommand = cleanedCommand;
        if (cleanedPrefixCommand) {
            effectiveCommand = cleanedPrefixCommand.prefix;
            additionalInputContent = cleanedPrefixCommand.phrase;
        }

        if (isPaused) {
            const isPausedCommand = voicePausedCustomControlsRef.current.has(effectiveCommand);
            if (!isPausedCommand) return null;
        } else {
            const isDictationCommand = voiceDictationCustomControlsRef.current.has(effectiveCommand);
            if (!isDictationCommand) return null;
        }

        return findCustomControl(effectiveCommand, currentDeviceId, (command) => true, additionalInputContent);
    }

    function getPartialControlMacro(
        command: string,
        currentDeviceId: string,
        additionalInputContent?: string,
        isEligibleControl?: (control: CustomControlModel) => boolean
    ) {
        // find controls that are not prefix or dictation controls
        const cleanedCommand = cleanCommand(command);
        if (cleanedCommand.length === 0) return null;

        const isEligible = isEligibleControl ?? ((control: CustomControlModel) => true);

        return findCustomControl(
            cleanedCommand,
            currentDeviceId,
            (control) => !control.isPrefixVoiceCommand && !control.allowWhenDictating && isEligible(control),
            additionalInputContent
        );
    }

    function getAllCustomCommandsMap() {
        const map = new Map<string, { id: string; source: string; value: string; name: string; deviceName?: string }>();
        if (!availableCustomControlsRef.current) return map;
        for (const deviceControls of availableCustomControlsRef.current) {
            if (deviceControls.controls) {
                for (const control of deviceControls.controls) {
                    if (control.commands) {
                        for (const command of control.commands) {
                            const key = deviceControls.deviceId + randomUUID();
                            map.set(key, {
                                id: control.id,
                                source: deviceControls.deviceId,
                                value: command.toLowerCase(),
                                name: control.name,
                                deviceName: deviceControls.name,
                            });
                        }
                    }
                }
            }
        }
        return map;
    }

    function clear() {
        setAvailableCustomControls([]);
        setProfileCustomControls([]);
        voicePausedCustomControlsRef.current.clear();
        handleVoiceDictationControlsUpdate(new Set());
        voicePrefixCustomControlsRef.current.clear();
        localStorage.removeItem('CUSTOM_CONTROLS');
    }

    async function resetCustomControls(localePreferences: UpdateUserLocaleRequest) {
        const locale = localePreferences.defaultVoiceLocale;
        customControlsLocaleRef.current = locale;

        const interfaceResult = await enabledApi.updateUserAppInterfaceControls([], locale);
        if (interfaceResult?.resultType === 'Ok' && interfaceResult.data) {
            updateLocalAppInterfaceControls(interfaceResult.data);
        }

        const devices = deviceContainer.devicesRef.current;
        if (devices) {
            for (let device of devices) {
                if (device.isVerified === false) continue;
                const result = await enabledApi.updateDeviceCustomControls(device.id, [], locale);
                if (result?.resultType === 'Ok' && result.data) {
                    setResetDeviceControls(true);
                    updateLocalDeviceCustomControls(result.data);
                }
            }
        }
    }

    function removeDeviceControls(deviceId: string) {
        const updatedAvailableControls =
            availableCustomControlsRef.current?.filter((d) => d.deviceId !== deviceId) || null;
        setAvailableCustomControls(updatedAvailableControls);
        const updatedProfileControls = profileCustomControlsRef.current?.filter((d) => d.deviceId !== deviceId) || null;
        setProfileCustomControls(updatedProfileControls);
        resetVoiceCustomControls();
        snapshotState();
    }

    async function addDeviceControls(device: UserDeviceModel) {
        const locale = customControlsLocaleRef.current;
        try {
            setIsLoading(true);

            const deviceType = device.device?.deviceType;

            // Seed with device type controls if none exist
            if (
                deviceType &&
                device.customControls.length === 0 &&
                device.customDeviceTypeControlOverrides.length === 0
            ) {
                const deviceTypeControlsResult = await enabledApi.getDeviceTypeCustomControls(deviceType.id, locale);
                if (deviceTypeControlsResult?.resultType === 'Ok' && deviceTypeControlsResult.data) {
                    device.customControls = deviceTypeControlsResult.data.controls.map(
                        (c) => ({ ...c, isDeviceTypeControl: true }) as CustomControlModel
                    );
                }
            }

            // If still no controls, nothing to add
            if (device.customControls.length === 0 && device.customDeviceTypeControlOverrides.length === 0) {
                return new SuccessResult<boolean>(true);
            }

            const deviceId = device.id;
            const deviceName = deviceContainer.getDeviceName(deviceId);
            const isPrivate = deviceType?.isPrivate;

            const controls = getCustomControlModels(device.customControls, device.customDeviceTypeControlOverrides);

            const newEntry: AvailableCustomControlsModel = {
                deviceId,
                deviceTypeId: deviceType.id,
                name: deviceName,
                isPrivate,
                controls,
            };

            setAvailableCustomControls((prev) => [...(prev ?? []), newEntry]);

            const profileControls = controls.filter((c) => c.isProfileControl);
            if (profileControls.length > 0) {
                setProfileCustomControls((prev) => [...(prev ?? []), { ...newEntry, controls: profileControls }]);
            }

            snapshotState();
            return new SuccessResult<boolean>(true);
        } catch (error) {
            console.log(error);
            setErrors(errors);
            return new UnexpectedResult<boolean>();
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const unsubDeviceAdded = subscribe('device-added', (data) => {
            addDeviceControls(data.device);
        });

        const unsubDeviceRemoved = subscribe('remove-device', (data) => {
            removeDeviceControls(data.device.id);
        });

        return () => {
            unsubDeviceAdded();
            unsubDeviceRemoved();
        };
    }, []);

    return {
        initializeFromStorage,
        initializeFromRemote,
        availableCustomControls,
        availableCustomControlsRef,
        devicesCountRef,
        errors,
        profileCustomControls,
        profileCustomControlsRef,
        voicePausedCustomControlsRef,
        voiceDictationCustomControlsRef,
        voicePrefixCustomControlsRef,
        updateAppInterfaceControls,
        updateLocalAppInterfaceControls,
        updateDeviceCustomControls,
        updateLocalDeviceCustomControls,
        getDeviceControlName,
        getFinalControlMacro,
        getFinalDictationControlMacro,
        getPartialControlMacro,
        resetVoiceCustomControls,
        getAllCustomCommandsMap,
        currentDeviceControls,
        clear,
        isLoading,
        resetCustomControls,
        customControlsLocaleRef,
        resetDeviceControls,
        setResetDeviceControls,
        getDeviceCustomControls,
        updateCustomUserDeviceControls,
        setAvailableCustomControls,
        snapshotState,
    };
}

const GlobalCustomControlsContainer = createContainer(useGlobalCustomControlsContainer);
export default GlobalCustomControlsContainer;
